import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { DiaDaRegua, Regua as ReguaDados } from '@/api/regua';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { acervoQueVence, aoMeioDia, diaEMes, diaInicial, plural, rotuloCurto, rotuloLongo, trechos } from './reguaDias';

/** Hachura do dia que não conta: o prazo salta por cima dele. */
const HACHURA = 'bg-[repeating-linear-gradient(135deg,var(--color-border)_0_3px,transparent_3px_7px)]';
/** Altura da barra por processo, com piso visível e teto — o maior dia não vira bloco cheio quando vale 2. */
const PX_POR_PROCESSO = 14;
const ALTURA_MINIMA = 10;
const ALTURA_MAXIMA = 96;

function numero(d: DiaDaRegua): string {
  // Estimado, como no selo da intimação: "~".
  return d.estimados > 0 ? `~${d.processos}` : String(d.processos);
}

function frase(d: DiaDaRegua): string {
  if (d.processos === 0) return 'nenhum processo com prazo.';
  const estimado = d.estimados > 0 ? ` (${plural(d.estimados, 'vencimento estimado', 'vencimentos estimados')})` : '';
  return `${plural(d.processos, 'processo com prazo', 'processos com prazo')}${estimado}.`;
}

/**
 * A RÉGUA (do Rubens, Bloco F de docs/PLANO_CONTAGEM_PRAZOS.md): quantos processos
 * têm prazo vencendo em cada dia das próximas duas semanas.
 *
 * Não é calendário: prazo é data-limite para produzir uma peça; a pergunta é
 * quanto cai em cada dia. O dia de zero fica — é onde dá para respirar. O dia que
 * não conta (fim de semana, feriado, suspensão) é um vão estreito e hachurado,
 * com o motivo de pé: é ele que explica o salto do prazo. Quem diz se o dia conta
 * é o backend, pelo calendário do usuário.
 *
 * Larga, deita em colunas: passar o mouse (ou o foco) mostra o dia na linha de
 * cima, e o clique já abre o acervo filtrado — um clique só. Estreita (medida pelo
 * contêiner, não pela janela), vira lista: tocar abre o acervo.
 */
export function Regua({ regua, loading }: { regua: ReguaDados | undefined; loading: boolean }) {
  const navigate = useNavigate();
  const [marcado, setMarcado] = useState<string | null>(null);

  if (loading) return <Skeleton className="h-64 w-full rounded-lg" />;
  if (!regua || regua.dias.length === 0) return null;

  const { hoje, dias } = regua;
  const selecionado = dias.find((d) => d.data === (marcado ?? diaInicial(dias))) ?? dias[0];
  const partes = trechos(dias);
  const feriados = dias.filter((d) => !d.conta && d.motivo && !/^(sábado|domingo)$/.test(d.motivo));
  const ressalvas = dias.flatMap((d) => d.ressalvas.map((r) => `${diaEMes(d.data)} ${r}`));

  return (
    <section
      aria-labelledby="regua-titulo"
      className="@container rounded-xl bg-accent px-4 py-6 text-accent-foreground sm:px-8"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="regua-titulo" className="text-lg font-semibold">
          Prazos por dia, nas próximas 2 semanas
        </h2>
        <p className="text-sm text-muted-foreground">
          <span className="text-xl font-bold text-primary tabular-nums">{regua.processos_na_janela}</span>{' '}
          {regua.processos_na_janela === 1 ? 'processo' : 'processos'} em duas semanas
        </p>
      </div>

      {/* O dia marcado, em palavras, com o caminho para os processos (forma larga). */}
      <p className="mt-1 hidden text-sm @3xl:block" aria-live="polite">
        <span className="font-medium">{rotuloLongo(selecionado.data, hoje)}</span>
        <span className="text-muted-foreground"> — {frase(selecionado)}</span>
        {selecionado.processos > 0 && (
          <>
            {' '}
            <Link
              to={acervoQueVence(selecionado.data)}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Ver {plural(selecionado.processos, 'processo', 'processos')}
            </Link>
          </>
        )}
      </p>

      {/* FORMA LARGA: colunas e vãos. */}
      <div className="mt-4 hidden overflow-x-auto pb-2 @3xl:block">
        <div className="flex min-w-max items-stretch" onMouseLeave={() => setMarcado(null)}>
          {partes.map((t) =>
            t.tipo === 'vao' ? (
              <div key={t.dias[0].data} className="flex shrink-0 flex-col px-1 pt-1" aria-hidden="true">
                <div className="h-5 rounded-t-full border-x border-t border-dashed border-primary/40" />
                <div className="flex h-28 justify-center gap-1">
                  {t.dias.map((d) => (
                    <span
                      key={d.data}
                      title={`${diaEMes(d.data)}: ${d.motivo}`}
                      className={cn(
                        'max-h-28 overflow-hidden whitespace-nowrap text-[11px] [transform:rotate(180deg)] [writing-mode:vertical-rl]',
                        d.data === hoje ? 'font-semibold text-primary' : 'text-muted-foreground',
                      )}
                    >
                      {d.motivo}
                    </span>
                  ))}
                </div>
                <div className={cn('h-2', HACHURA)} />
                <div className="mt-2 flex justify-center gap-1.5 text-xs tabular-nums text-muted-foreground">
                  {t.dias.map((d) => (
                    <span key={d.data} className={cn(d.data === hoje && 'font-semibold text-primary')}>
                      {aoMeioDia(d.data).getDate()}
                    </span>
                  ))}
                </div>
                {/* Hoje também num vão (a suspensão de hoje): o dia não some da régua. */}
                {t.dias.some((d) => d.data === hoje) && (
                  <span className="mt-1 text-center text-xs font-semibold text-primary">hoje</span>
                )}
              </div>
            ) : (
              <Button
                key={t.dia.data}
                variant="ghost"
                onMouseEnter={() => setMarcado(t.dia.data)}
                onFocus={() => setMarcado(t.dia.data)}
                onClick={() => (t.dia.processos > 0 ? navigate(acervoQueVence(t.dia.data)) : setMarcado(t.dia.data))}
                aria-label={`${rotuloLongo(t.dia.data, hoje)}: ${frase(t.dia)}${t.dia.processos > 0 ? ' Abrir no acervo.' : ''}`}
                className={cn(
                  'flex h-auto min-w-[4.5rem] flex-1 flex-col items-stretch gap-0 rounded-xl px-1 pb-2 pt-1 font-normal hover:bg-border/50',
                  t.dia.data === selecionado.data && 'bg-border hover:bg-border',
                )}
              >
                <span className="h-5 text-center text-xs tabular-nums text-muted-foreground">
                  {t.dia.processos > 0 ? numero(t.dia) : ''}
                </span>
                <span className="flex h-28 items-end justify-center border-b border-foreground/30">
                  {t.dia.processos > 0 && (
                    <span
                      className="block w-9 rounded-t-sm bg-primary"
                      style={{
                        height: Math.min(ALTURA_MAXIMA, Math.max(ALTURA_MINIMA, t.dia.processos * PX_POR_PROCESSO)),
                      }}
                    />
                  )}
                </span>
                <span
                  className={cn(
                    'mt-2 text-center text-xl font-semibold tabular-nums leading-none',
                    t.indice === 0 && 'text-primary',
                  )}
                >
                  {aoMeioDia(t.dia.data).getDate()}
                </span>
                <span
                  className={cn('mt-1 text-center text-xs', t.indice === 0 ? 'text-primary' : 'text-muted-foreground')}
                >
                  {rotuloCurto(t.dia.data, hoje)}
                </span>
              </Button>
            ),
          )}
        </div>
      </div>

      {/* FORMA ESTREITA: a mesma régua, deitada em linhas. */}
      <ul className="mt-3 @3xl:hidden">
        {dias.map((d, i) =>
          !d.conta ? (
            <li
              key={d.data}
              className={cn('my-1 flex min-h-8 items-center rounded-sm px-3 text-xs text-muted-foreground', HACHURA)}
            >
              {diaEMes(d.data)} — {d.motivo}
            </li>
          ) : (
            <li key={d.data}>
              <Button
                variant="ghost"
                disabled={d.processos === 0}
                onClick={() => navigate(acervoQueVence(d.data))}
                aria-label={`${rotuloLongo(d.data, hoje)}: ${frase(d)}`}
                className="grid h-auto min-h-11 w-full grid-cols-[5.5rem_2.5rem_1fr] items-center gap-3 px-1 text-left font-normal disabled:opacity-100"
              >
                <span className={cn('text-sm', i === 0 ? 'font-medium text-primary' : 'text-muted-foreground')}>
                  {rotuloCurto(d.data, hoje)}, {aoMeioDia(d.data).getDate()}
                </span>
                <span
                  className={cn(
                    'text-right tabular-nums',
                    d.processos === 0 ? 'text-sm text-muted-foreground/70' : 'text-lg font-bold',
                  )}
                >
                  {d.processos === 0 ? '—' : numero(d)}
                </span>
                {d.processos === 0 ? (
                  <span className="text-xs text-muted-foreground/70">sem prazo</span>
                ) : (
                  <span
                    aria-hidden="true"
                    className="block h-2.5 rounded-full bg-primary"
                    style={{ width: `${Math.min(100, d.processos * 12)}%` }}
                  />
                )}
              </Button>
            </li>
          ),
        )}
      </ul>

      {/* O SALTO, em palavras: o vão mostra; esta linha diz o que ele significa. */}
      {(feriados.length > 0 || ressalvas.length > 0) && (
        <div className="mt-3 space-y-1 text-xs leading-relaxed text-muted-foreground">
          {feriados.map((f) => (
            <p key={f.data}>
              {diaEMes(f.data)} não conta ({f.motivo}): nenhum prazo vence nele, e a contagem salta para o próximo dia
              útil.
            </p>
          ))}
          {ressalvas.map((r) => (
            <p key={r}>{r}.</p>
          ))}
        </div>
      )}
    </section>
  );
}
